// =============================================================================
// PERIOD.JS — Which month a deal belongs to, and how much of it was collected.
// =============================================================================
// Two bugs lived here before this file existed.
//
// 1. YEARS GOT MIXED. deals.month is a bare month NAME ("January") with no year
//    in it. Reports grouped revenue by that string and the commission screen
//    summed an agent's monthly sales by it, so January 2025 and January 2026
//    landed in the same bucket. For a plain revenue chart that is misleading;
//    for Lydie's $20,000 monthly qualifier it is worse, because two separate
//    Januarys of $10K each would add up and wrongly unlock a month's
//    commission. deals.date carries the real date, so the period comes from
//    there and `month` is left alone as a display label.
//
// 2. PARTIAL PAYMENTS COUNTED AS NOTHING. Collection totals asked
//    isPaidStatus(), which is true only for Paid/Done. A deal sitting at
//    "Partially Paid" with $4,000 of $5,000 banked contributed $0 to collected
//    revenue, so the reports understated cash received.
// =============================================================================

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]
const MONTH_SHORT = MONTH_NAMES.map(m => m.slice(0, 3))

/** True for the statuses that mean the client paid in full. */
function isFullyPaid(status) {
  return status === 'Paid/Done' || status === 'Paid' || status === 'Done'
}

/**
 * The 'YYYY-MM' period a deal belongs to, taken from deals.date.
 * Returns null when the deal has no usable date — callers group those
 * separately rather than silently folding them into a real month.
 */
export function dealPeriod(deal) {
  const raw = deal?.date
  if (!raw) return null
  // Dates are stored as plain 'YYYY-MM-DD'. Slicing avoids new Date(), which
  // would read a bare date as UTC midnight and shift it a day backwards in
  // Montreal — enough to move a 1st-of-the-month deal into the month before.
  const text = String(raw)
  const match = /^(\d{4})-(\d{2})/.exec(text)
  if (match) return `${match[1]}-${match[2]}`
  const parsed = new Date(text)
  if (Number.isNaN(parsed.getTime())) return null
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}`
}

/** The four-digit year of a deal, or null. */
export function dealYear(deal) {
  const period = dealPeriod(deal)
  return period ? period.slice(0, 4) : null
}

/** The month index (1-12) of a period, or null. */
export function periodMonthIndex(period) {
  if (!period) return null
  const n = Number(String(period).slice(5, 7))
  return n >= 1 && n <= 12 ? n : null
}

/** 'Jan 2026' — unambiguous even when a chart spans several years. */
export function periodLabel(period) {
  const idx = periodMonthIndex(period)
  if (!idx) return 'Undated'
  return `${MONTH_SHORT[idx - 1]} ${String(period).slice(0, 4)}`
}

/** 'Jan' — for charts already scoped to a single year. */
export function periodShortMonth(period) {
  const idx = periodMonthIndex(period)
  return idx ? MONTH_SHORT[idx - 1] : 'Undated'
}

/** 'January' — matches the legacy deals.month spelling. */
export function periodMonthName(period) {
  const idx = periodMonthIndex(period)
  return idx ? MONTH_NAMES[idx - 1] : 'Undated'
}

/** Every period present in a deal list, oldest first. Undated deals excluded. */
export function periodsOf(deals) {
  const seen = new Set()
  ;(deals || []).forEach(d => {
    const p = dealPeriod(d)
    if (p) seen.add(p)
  })
  return [...seen].sort()
}

/** Every year present in a deal list, newest first. */
export function yearsOf(deals) {
  const seen = new Set()
  ;(deals || []).forEach(d => {
    const y = dealYear(d)
    if (y) seen.add(y)
  })
  return [...seen].sort().reverse()
}

/**
 * Dollars actually received on a deal.
 *
 * Paid/Done asserts the invoice was settled, so it counts in full regardless of
 * any stale partial figure left over from when it was still being collected.
 * Partially Paid counts whatever was recorded in amount_collected, and nothing
 * if no figure was entered yet. Every earlier status has collected nothing.
 */
export function collectedAmount(deal) {
  if (!deal) return 0
  if (isFullyPaid(deal.status)) return Number(deal.deal_amount) || 0
  if (deal.status === 'Partially Paid') {
    const raw = deal.amount_collected
    if (raw === null || raw === undefined || raw === '') return 0
    const n = Number(raw)
    if (!Number.isFinite(n) || n < 0) return 0
    // Never report more collected than the deal is worth.
    const total = Number(deal.deal_amount) || 0
    return total > 0 ? Math.min(n, total) : n
  }
  return 0
}

/** Collected dollars across a list of deals. */
export function totalCollected(deals) {
  return (deals || []).reduce((sum, d) => sum + collectedAmount(d), 0)
}
